import './css/guidelines.css';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const Guidelines = () => {
    const name = process.env.REACT_APP_APP_NAME || "ExamWorld";
    const [activeSection, setActiveSection] = useState('academic');
    
    const sections = [
        { id: 'academic', label: 'Academic Integrity', icon: 'fa-solid fa-graduation-cap' },
        { id: 'behavior', label: 'Community Rules', icon: 'fa-solid fa-users' },
        { id: 'technical', label: 'Tech Guidelines', icon: 'fa-solid fa-laptop-code' },
        { id: 'privacy', label: 'Privacy & Security', icon: 'fa-solid fa-shield-halved' },
        { id: 'support', label: 'Support', icon: 'fa-solid fa-headset' }
    ];

    const scrollToSection = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    return (
        <div className="guidelinesPageWrapper">
            
            {/* Sticky Navigation */}
            <nav className="guidelinesNav">
                <div className="navContainer">
                    <div className="navBrand">
                        <span className="navLogo">{name} Guidelines</span>
                    </div>
                    <div className="navSections">
                        {sections.map(section => (
                            <button
                                key={section.id}
                                className={`navLinkBtn ${activeSection === section.id ? 'activeNav' : ''}`}
                                onClick={() => scrollToSection(section.id)}
                            >
                                <i className={section.icon}></i>
                                <span>{section.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </nav>

            <div className="guidelinesContentArea">
                
                {/* Header */}
                <header className="guidelinesHeader">
                    <h1>Platform Guidelines</h1>
                    <p>A clear roadmap to maximize your learning experience while maintaining academic excellence and community harmony.</p>
                </header>

                {/* Quick Actions Bar */}
                <div className="quickActionsGrid">
                    <div className="quickActionCard">
                        <i className="fa-solid fa-clipboard-list actionIcon textPrimary"></i>
                        <div className="actionInfo">
                            <h4>Quick Checklist</h4>
                            <p>Essential rules every student must follow</p>
                        </div>
                    </div>
                    <div className="quickActionCard">
                        <i className="fa-solid fa-book-open actionIcon textPrimary"></i>
                        <div className="actionInfo">
                            <h4>Study Resources</h4>
                            <p>Access learning materials and tutorials</p>
                        </div>
                    </div>
                    <div className="quickActionCard">
                        <i className="fa-solid fa-user-shield actionIcon textPrimary"></i>
                        <div className="actionInfo">
                            <h4>Safety First</h4>
                            <p>Protect your account and data</p>
                        </div>
                    </div>
                </div>

                {/* 01. Academic Integrity */}
                <section id="academic" className="guidelineSection modernCard">
                    <div className="sectionTitleBox">
                        <h3><span className="sectionNumber">01</span> Academic Integrity</h3>
                        <p>Upholding Honesty & Excellence</p>
                    </div>
                    
                    <div className="splitLayout">
                        <div className="splitLeft">
                            <div className="contentCard primaryCard">
                                <h4><i className="fa-solid fa-check-circle textSuccess"></i> Your Responsibilities</h4>
                                <ul className="customList">
                                    <li>Complete all tests independently</li>
                                    <li>Adhere strictly to time limits</li>
                                    <li>Use only permitted resources</li>
                                    <li>Maintain confidentiality of questions</li>
                                </ul>
                            </div>
                            <div className="infoAlert">
                                <i className="fa-solid fa-lightbulb"></i>
                                <p><strong>Pro Tip:</strong> Use our practice tests to build confidence before attempting timed assessments.</p>
                            </div>
                        </div>
                        
                        <div className="splitRight">
                            <div className="contentCard warningCard">
                                <h4><i className="fa-solid fa-triangle-exclamation textDanger"></i> Strictly Prohibited</h4>
                                <ul className="customList dangerList">
                                    <li>Plagiarism or copying answers</li>
                                    <li>Using unauthorized assistance</li>
                                    <li>Multiple account creation</li>
                                    <li>Sharing test materials</li>
                                </ul>
                            </div>
                            <div className="timelineBox">
                                <h4>Consequence Timeline</h4>
                                <div className="timelineStep">
                                    <span className="stepNum">1</span>
                                    <div><strong>First Warning:</strong> Score invalidation & warning</div>
                                </div>
                                <div className="timelineStep">
                                    <span className="stepNum">2</span>
                                    <div><strong>Temporary Ban:</strong> 7-day account suspension</div>
                                </div>
                                <div className="timelineStep">
                                    <span className="stepNum">3</span>
                                    <div><strong>Permanent Removal:</strong> Account termination</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 02. Community Guidelines */}
                <section id="behavior" className="guidelineSection modernCard">
                    <div className="sectionTitleBox">
                        <h3><span className="sectionNumber">02</span> Community Guidelines</h3>
                        <p>Building a Supportive Learning Space</p>
                    </div>
                    
                    <div className="behaviorGrid">
                        <div className="contentCard successCard">
                            <div className="badge badgeSuccess">Do's</div>
                            <ul className="customList">
                                <li><strong>Respect:</strong> Treat all community members nicely.</li>
                                <li><strong>Language:</strong> Use encouraging and polite language.</li>
                                <li><strong>Knowledge:</strong> Share knowledge constructively.</li>
                                <li><strong>Feedback:</strong> Provide helpful feedback.</li>
                            </ul>
                        </div>
                        <div className="contentCard warningCard">
                            <div className="badge badgeDanger">Don'ts</div>
                            <ul className="customList dangerList">
                                <li><strong>Harassment:</strong> Bullying will not be tolerated.</li>
                                <li><strong>Inappropriate:</strong> Sharing explicit content.</li>
                                <li><strong>Impersonate:</strong> Pretending to be someone else.</li>
                                <li><strong>Privacy:</strong> Sharing private information.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* 03. Technical Setup */}
                <section id="technical" className="guidelineSection modernCard">
                    <div className="sectionTitleBox">
                        <h3><span className="sectionNumber">03</span> Technical Setup</h3>
                        <p>Optimal Performance Configuration</p>
                    </div>
                    
                    <div className="techDashboardGrid">
                        <div className="contentCard">
                            <h4>System Requirements</h4>
                            <div className="reqGrid">
                                <div className="reqItem">
                                    <i className="fa-solid fa-wifi textPrimary"></i>
                                    <div><span>Internet Speed</span><strong>2+ Mbps</strong></div>
                                </div>
                                <div className="reqItem">
                                    <i className="fa-solid fa-battery-full textPrimary"></i>
                                    <div><span>Battery Backup</span><strong>2+ Hours</strong></div>
                                </div>
                                <div className="reqItem">
                                    <i className="fa-brands fa-chrome textPrimary"></i>
                                    <div><span>Browser</span><strong>Chrome 80+</strong></div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="contentCard">
                            <h4>Pre-Test Checklist</h4>
                            <div className="checkboxList">
                                <label><input type="checkbox" defaultChecked /> Stable internet connection</label>
                                <label><input type="checkbox" defaultChecked /> Browser cache cleared</label>
                                <label><input type="checkbox" /> Device fully charged</label>
                                <label><input type="checkbox" /> Quiet environment</label>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 04. Privacy & Security */}
                <section id="privacy" className="guidelineSection modernCard">
                    <div className="sectionTitleBox">
                        <h3><span className="sectionNumber">04</span> Privacy & Security</h3>
                        <p>Your Data, Our Responsibility</p>
                    </div>
                    
                    <div className="securityGrid">
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-shield-halved securityIcon textPrimary"></i>
                            <h4>Your Safety</h4>
                            <p>Strong passwords, session timeouts, and encrypted connections.</p>
                        </div>
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-lock securityIcon textPrimary"></i>
                            <h4>Data Protection</h4>
                            <p>End-to-end encryption, GDPR compliance, and regular backups.</p>
                        </div>
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-eye securityIcon textPrimary"></i>
                            <h4>Transparent Usage</h4>
                            <p>Clear privacy policy, usage insights, and opt-out options.</p>
                        </div>
                    </div>
                </section>

                {/* 05. Support Center */}
                <section id="support" className="guidelineSection modernCard">
                    <div className="sectionTitleBox">
                        <h3><span className="sectionNumber">05</span> Support Center</h3>
                        <p>We're Here to Help You Succeed</p>
                    </div>
                    
                    <div className="supportGrid">
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-envelope supportIcon textPrimary"></i>
                            <h4>Email Support</h4>
                            <p className="textMuted">24-hour response time</p>
                            <a href={`mailto:support@${name.toLowerCase().replace(/\s+/g, '')}.in`} className="supportLink">
                                support@{name.toLowerCase().replace(/\s+/g, '')}.in
                            </a>
                        </div>
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-book supportIcon textPrimary"></i>
                            <h4>Knowledge Base</h4>
                            <p className="textMuted">Self-help resources</p>
                            <Link to="/footer/help" className="supportLink">Browse FAQs →</Link>
                        </div>
                        <div className="contentCard textCenter">
                            <i className="fa-solid fa-video supportIcon textPrimary"></i>
                            <h4>Video Tutorials</h4>
                            <p className="textMuted">Step-by-step guides</p>
                            <Link to="/footer/guide" className="supportLink">Read User Guide →</Link>
                        </div>
                    </div>
                </section>

                {/* Final Agreement */}
                <section className="agreementSection modernCard textCenter">
                    <h2>Ready to Begin Your Journey?</h2>
                    <p className="textMuted">By continuing, you agree to our guidelines and policies</p>
                    
                    <div className="agreementActions">
                        <Link to="/footer/policy" className="actionBtn secondaryBtn">Review Policies</Link>
                        <Link to="/profile" className="actionBtn primaryBtn">
                            Start Learning <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                    </div>
                    <p className="versionInfo">Guidelines Version 1.0.1 • Updated January 2026</p>
                </section>
                
            </div>
        </div>
    );
};

export default Guidelines;